/* ── What is switched on, where ─────────────────────────────────────────
   One place that answers "is this environment production", and one flag per
   feature that isn't ready for it.

   ── WHY HOSTNAME, NOT AN ENV VAR ──
   A `REACT_APP_*` flag would have to be set in the prod Netlify dashboard, and
   the failure mode of forgetting is that the unfinished feature ships to paying
   clients. This fails the other way: prod is off unless the code says otherwise,
   and nobody can forget a step that doesn't exist.

   It also keeps turning a feature ON in prod where it belongs — a deliberate,
   reviewed, one-line change that goes through `staging-buffer -> develop ->
   main` like everything else, rather than a dashboard toggle with no history.

   Precedent: `embedHost()` in publishEmbedFlow.ts already derives the
   environment from the hostname the same way.
*/

/**
 * Is this the production app?
 *
 * Deliberately NOT the same test as `embedHost()`, which treats every
 * `*.netlify.app` as stage. That is fine for choosing an embed host, but
 * `openskyprod.netlify.app` IS production (docs/ENVIRONMENTS.md §1) and it
 * would be a bad way to find that out.
 *
 * Order matters: prod's Netlify name is checked before the `.netlify.app`
 * catch-all, and `stage.` before the `openskyvr.com` suffix — otherwise
 * `stage.openskyvr.com` reads as production.
 */
export const isProdHost = (host: string): boolean => {
  const h = (host || "").trim().toLowerCase();
  if (!h) return false;                       // no window (SSR, tests) — never prod
  if (h === "openskyprod.netlify.app") return true;
  if (/(^|\.)stage\./.test(h)) return false;  // stage.openskyvr.com, embed.stage.…
  /* dev.openskyvr.com is a developer's own machine published through a
     named Cloudflare tunnel — the permanent replacement for throwaway
     trycloudflare hostnames, which changed on every restart and needed a
     fresh Keycloak redirect URI each time. It is an openskyvr.com name,
     so the catch-all at the bottom called it PRODUCTION and hid every
     unreleased feature on the one host built for testing them. */
  if (/(^|\.)dev\./.test(h)) return false;    // dev.openskyvr.com
  if (h === "localhost" || h === "127.0.0.1") return false;
  if (h.endsWith(".netlify.app")) return false;   // openskystage + deploy previews
  return /(^|\.)openskyvr\.com$/.test(h);
};

/**
 * Guided tours — the live host-and-buyer walkthrough room.
 *
 * OFF IN PRODUCTION, on founder's call (2026-09-28): the feature works on stage
 * but is not finished — the backend recording branch is still awaiting its MR,
 * and the family-pass admission path still queues everyone in the lobby.
 *
 * This hides the entry point AND the room routes, so a stray `/room/…` or
 * `/visit/…` link cannot render a half-working room in front of a paying
 * client. Nothing is deleted: the code ships, it just isn't reachable, so
 * turning it on is this one function returning true.
 *
 * Delete this flag (and its test) when guided tours ship to prod.
 */
/* ON FOR STAGE AND LOCALHOST AGAIN, 2026-10-01, on the founder's call: the room
 * screens were rebuilt for phones and tablets and he needs to test them on a real
 * phone, which cannot be done while the routes fall through to the projects home.
 *
 * It was off everywhere from 2026-09-30, because client demos are run on stage and
 * the Guided Tour button sat in the project header through an Ankura demo. That risk
 * has not gone away — it is accepted for the duration of this testing, and whoever is
 * demoing on stage should know the button is back.
 *
 * PRODUCTION IS STILL OFF, which is the part that must not drift.
 *
 * TO TURN IT OFF EVERYWHERE AGAIN — one line, and the test below flips with it:
 *     export const guidedTourEnabled = (host?: string): boolean => false;
 */
export const guidedTourEnabled = (host?: string): boolean =>
  !isProdHost(host !== undefined ? host : currentHost());

/** The browser's hostname, or "" anywhere there is no browser. */
export const currentHost = (): string =>
  typeof window !== "undefined" && window.location ? window.location.hostname : "";
